import { useMemo, useRef, useEffect } from 'react';
import { TextField } from '@/shared/components/TextField';
import { useAddUser } from '../../hooks/useAddUser';
import CreateLayout from '@/shared/layout/createLayout';
import { useUserPrivileges } from '@/shared/hooks/useUserPrivileges';
import ErrorPage403 from '@/app/routes/errorPage403';
import Loader from '@/app/routes/Loader';
import type { AddUserDetails } from '../../types/addUserType';
import type { AdminPrivileges } from '../../types/adminPrivilegeTypes';
import AdminPrivilegesSection from '../../components/AdminPrivilegesSection';

const AddAdminHeader = () => (
  <p className="text-titleLarge text-neutral-10 ">Dodaj administratora</p>
);

type AddAdminFormProps = {
  formData: AddUserDetails;
  errors: AddUserDetails;
  isSubmitting: boolean;
  privileges: AdminPrivileges;
  privilegesError: string;
  submitErrorCount: number;
  handleNameChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleEmailChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handlePhoneChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleAddress1Change: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleAddress2Change: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleCityChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleStateChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleZipChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handlePrivilegesChange: (p: AdminPrivileges) => void;
};

const AddUser = () => {
  const {
    snackbarError,
    isSnackbarOpen,
    handleCloseSnackbar,
    handleSubmit,
    handleCancel,
    isCancelModalOpen,
    handleCloseCancelModal,
    handleConfirmCancel,
    formData,
    errors,
    privileges,
    privilegesError,
    isSubmitting,
    submitErrorCount,
    handleNameChange,
    handleEmailChange,
    handlePhoneChange,
    handleAddress1Change,
    handleAddress2Change,
    handleCityChange,
    handleStateChange,
    handleZipChange,
    handlePrivilegesChange,
  } = useAddUser();

  const { privileges: callerPrivileges, isLoading: privilegesLoading } =
    useUserPrivileges();

  const AddAdminForm = useMemo(
    () =>
      function AddAdminForm({
        formData,
        errors,
        isSubmitting,
        privileges,
        privilegesError,
        submitErrorCount,
        handleNameChange,
        handleEmailChange,
        handlePhoneChange,
        handleAddress1Change,
        handleAddress2Change,
        handleCityChange,
        handleStateChange,
        handleZipChange,
        handlePrivilegesChange,
      }: AddAdminFormProps) {
        const nameRef = useRef<HTMLDivElement>(null);
        const emailRef = useRef<HTMLDivElement>(null);
        const phoneRef = useRef<HTMLDivElement>(null);
        const address1Ref = useRef<HTMLDivElement>(null);
        const cityRef = useRef<HTMLDivElement>(null);
        const stateRef = useRef<HTMLDivElement>(null);
        const zipRef = useRef<HTMLDivElement>(null);
        const privilegesRef = useRef<HTMLDivElement>(null);

        // Keep a ref to the latest errors so the scroll effect can read them
        // without listing them as deps (which would fire on every keystroke).
        const errorsRef = useRef(errors);
        errorsRef.current = errors;
        const privilegesErrorRef = useRef(privilegesError);
        privilegesErrorRef.current = privilegesError;

        useEffect(() => {
          if (!submitErrorCount) {
            return;
          }
          const fieldsInOrder = [
            { ref: nameRef, error: errorsRef.current.name },
            { ref: emailRef, error: errorsRef.current.email },
            { ref: phoneRef, error: errorsRef.current.phone },
            { ref: address1Ref, error: errorsRef.current.address1 },
            { ref: cityRef, error: errorsRef.current.city },
            { ref: stateRef, error: errorsRef.current.state },
            { ref: zipRef, error: errorsRef.current.zip },
            { ref: privilegesRef, error: privilegesErrorRef.current },
          ];
          const firstError = fieldsInOrder.find(({ error }) => error);
          if (firstError?.ref.current) {
            firstError.ref.current.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }, [submitErrorCount]);

        return (
          <>
            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                Podstawowe dane
              </p>
              <div className="flex gap-4 ">
                <div ref={nameRef} className="w-full">
                  <TextField
                    fullWidth={true}
                    label="Imię i nazwisko"
                    autoTrimMaxLength={200}
                    value={formData.name}
                    onChange={handleNameChange}
                    error={Boolean(errors.name)}
                    errorMessage={errors.name}
                    disabled={isSubmitting}
                    autoFocus={true}
                  />
                </div>
                <div ref={emailRef} className="w-full">
                  <TextField
                    fullWidth={true}
                    label="Adres e-mail"
                    autoTrimMaxLength={100}
                    value={formData.email}
                    onChange={handleEmailChange}
                    error={Boolean(errors.email)}
                    errorMessage={errors.email}
                    disabled={isSubmitting}
                  />
                </div>
                <div ref={phoneRef} className="w-full">
                  <TextField
                    fullWidth={true}
                    label="Numer telefonu"
                    type="tel"
                    maxLength={9}
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    error={Boolean(errors.phone)}
                    errorMessage={errors.phone}
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </div>

            <div className="border-b-2 border-[#DCDDE0]" />

            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                Dane adresowe
              </p>
              <div className="flex flex-col gap-8">
                <div className="flex gap-4">
                  <div ref={address1Ref} className="w-full">
                    <TextField
                      fullWidth={true}
                      label="Adres (linia 1)"
                      autoTrimMaxLength={200}
                      value={formData.address1}
                      onChange={handleAddress1Change}
                      error={Boolean(errors.address1)}
                      errorMessage={errors.address1}
                      disabled={isSubmitting}
                    />
                  </div>
                  <TextField
                    fullWidth={true}
                    label={
                      <>
                        Adres (linia 2)
                        <span className="text-bodySmall font-normal">
                          {' '}
                          (Opcjonalne)
                        </span>
                      </>
                    }
                    dataTestId="Adres (linia 2)"
                    autoTrimMaxLength={200}
                    value={formData.address2}
                    onChange={handleAddress2Change}
                    disabled={isSubmitting}
                  />
                  <div ref={cityRef} className="w-full">
                    <TextField
                      fullWidth={true}
                      label="Miejscowość"
                      autoTrimMaxLength={200}
                      value={formData.city}
                      onChange={handleCityChange}
                      error={Boolean(errors.city)}
                      errorMessage={errors.city}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
                <div className="flex gap-4">
                  <div ref={stateRef} className="w-full">
                    <TextField
                      fullWidth={true}
                      label="Województwo"
                      autoTrimMaxLength={200}
                      value={formData.state}
                      onChange={handleStateChange}
                      error={Boolean(errors.state)}
                      errorMessage={errors.state}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div ref={zipRef} className="w-full">
                    <TextField
                      fullWidth={true}
                      label={
                        <>
                          Kod pocztowy
                          <span className="text-bodySmall font-normal">
                            {' '}
                            (Opcjonalne)
                          </span>
                        </>
                      }
                      dataTestId="Kod pocztowy"
                      maxLength={200}
                      value={formData.zip}
                      onChange={handleZipChange}
                      error={Boolean(errors.zip)}
                      errorMessage={errors.zip}
                      disabled={isSubmitting}
                    />
                  </div>
                  <TextField
                    fullWidth={true}
                    label="Kraj"
                    maxLength={200}
                    value={formData.country}
                    disabled={true}
                  />
                </div>
              </div>
            </div>

            <div className="border-b-2 border-[#DCDDE0]" />

            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                Dostęp i uprawnienia
              </p>
              <div className="flex flex-col gap-8">
                <div className="flex gap-4 w-[32%]">
                  <TextField
                    fullWidth={true}
                    label="Rola"
                    maxLength={200}
                    disabled={true}
                    value={formData.role}
                  />
                </div>

                <div className="border-b-2 border-[#DCDDE0]" />

                <div ref={privilegesRef}>
                  <AdminPrivilegesSection
                    privileges={privileges}
                    onPrivilegesChange={handlePrivilegesChange}
                    disabled={isSubmitting}
                    privilegesError={privilegesError}
                  />
                </div>
              </div>
            </div>
          </>
        );
      },
    []
  );

  if (privilegesLoading) {
    return (
      <div className="flex items-center justify-center bg-white h-screen w-full">
        <Loader />
      </div>
    );
  }

  if (!callerPrivileges.adminUserCreate) {
    return <ErrorPage403 />;
  }

  return (
    <CreateLayout
      isSubmitting={isSubmitting}
      handleSubmit={handleSubmit}
      handleCancel={handleCancel}
      isCancelModalOpen={isCancelModalOpen}
      handleCloseCancelModal={handleCloseCancelModal}
      handleConfirmCancel={handleConfirmCancel}
      isSnackbarOpen={isSnackbarOpen}
      handleCloseSnackbar={handleCloseSnackbar}
      snackbarError={snackbarError}
      Header={AddAdminHeader}
    >
      <AddAdminForm
        formData={formData}
        errors={errors}
        isSubmitting={isSubmitting}
        privileges={privileges}
        privilegesError={privilegesError}
        submitErrorCount={submitErrorCount}
        handleNameChange={handleNameChange}
        handleEmailChange={handleEmailChange}
        handlePhoneChange={handlePhoneChange}
        handleAddress1Change={handleAddress1Change}
        handleAddress2Change={handleAddress2Change}
        handleCityChange={handleCityChange}
        handleStateChange={handleStateChange}
        handleZipChange={handleZipChange}
        handlePrivilegesChange={handlePrivilegesChange}
      />
    </CreateLayout>
  );
};
export default AddUser;
